// $FlowFixMe
import React, { Fragment, useMemo } from 'react';
import { reduxForm, Form, Field, formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import { pathOr } from 'ramda';
import { FormattedMessage, injectIntl } from 'react-intl';
import {
  renderCLCurrencyField,
  renderMultiSelectFieldFortress,
  renderInputFortress,
  renderSelectFortress,
} from '../../../../../utils/redux-form-helper';
import {
  Stack,
  Tooltip,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { AlertInfoIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import { useFlags } from 'launchdarkly-react-client-sdk';
import usePermissionsHook from '../../../../../components/ElementWithPermissions/usePermissionsHook';
import useAccessibilityTypesQuery from '../../../../../hooks/data-fetching/useAccessibilityTypesQuery';

import Spinner from '../../../../../components/Spinner';
import { FORM_NAME } from '../../constants';
import messages from '../messages';
import { useAsyncStateOptions } from '../hooks';
import validate from './validate';

type ReduxFormProps = {
  handleSubmit: Function,
};

type Props = ReduxFormProps & {
  onSubmit: Function,
  initialValues: Object,
  handleDeleteVehicle: Function,
  showXmlUnitNumberField: boolean,
  isPHFloorPlan: boolean,
  isHUDFloorPlan: boolean,
  selectedProperty: Object,
  change: Function,
  isADAValue: string,
  accessibilityTypeIdsValue: Array<string>,
  flags: Object,
  intl: Object,
};

const ADA_OPTIONS = [
  {
    value: 'true',
    text: 'Yes',
  },
  {
    value: 'false',
    text: 'No',
  },
];

// Two-column form grid: 27px vertical gap between rows (tightened per design review),
// 25px horizontal gap between the two fields; a lone field keeps half width.
const FieldRow = ({ children }: { children: any }) => (
  <Stack direction="row" sx={{ gap: '25px' }}>
    {children}
  </Stack>
);

const HalfField = ({ children }: { children: any }) => (
  <Stack sx={{ flex: '0 0 calc(50% - 12.5px)', minWidth: 0 }}>{children}</Stack>
);

const FullField = ({ children }: { children: any }) => (
  <Stack sx={{ flex: '1 1 100%', minWidth: 0 }}>{children}</Stack>
);

const GeneralUnitInformationForm = ({
  handleSubmit,
  initialValues,
  onSubmit,
  showXmlUnitNumberField,
  isPHFloorPlan,
  isHUDFloorPlan,
  selectedProperty,
  change,
  isADAValue,
  accessibilityTypeIdsValue,
  flags,
  intl,
}: Props) => {
  const unitOptions = useAsyncStateOptions();
  const featureFlags = useFlags();
  const hasUnitPricingEditPermission = usePermissionsHook({
    scope: ['unit-pricing-edit'],
  });

  const { removeUnitFloorLevelRestrictions } = flags;

  // Show Unit Price field if feature flag is enabled (regardless of availability)
  const showUnitPriceField = featureFlags?.unitLevelPrice;

  // ADA Accessibility Types (behind the ada-accessibility-types epic flag)
  const adaAccessibilityTypesEnabled = featureFlags?.adaAccessibilityTypes;
  const organizationId = pathOr(null, ['organizationId'], selectedProperty);
  const propertyId = pathOr(null, ['id'], selectedProperty);
  const { options: accessibilityTypeOptions } = useAccessibilityTypesQuery({
    organizationId,
    propertyId,
    options: {
      enabled: Boolean(
        adaAccessibilityTypesEnabled && organizationId && propertyId,
      ),
    },
  });

  const isADAYes = isADAValue === 'true';
  const showAccessibilityTypesField = adaAccessibilityTypesEnabled && isADAYes;

  const handleADAChange = (value) => {
    // Clearing on ADA -> No within the edit session; not restored if flipped back.
    if (adaAccessibilityTypesEnabled && value !== 'true') {
      change('accessibilityTypeIds', []);
    }
  };

  // Disable field if unit is not available OR if user doesn't have permission (memoized)
  const disableUnitPriceField = useMemo(
    () =>
      !initialValues?.unitStatus?.isAvailable ||
      (featureFlags?.unitLevelPrice && !hasUnitPricingEditPermission),
    [
      initialValues?.unitStatus?.isAvailable,
      featureFlags?.unitLevelPrice,
      hasUnitPricingEditPermission,
    ],
  );

  return (
    <Fragment>
      {!unitOptions ? (
        <Spinner />
      ) : (
        <Form onSubmit={handleSubmit}>
          <Stack sx={{ gap: '27px' }}>
            {/* Unit Floor Level | Unit Quoting Rent Amount */}
            <FieldRow>
              <HalfField>
                <Field
                  name="unitFloorLevel"
                  component={renderInputFortress}
                  label={
                    removeUnitFloorLevelRestrictions
                      ? intl.formatMessage(messages.unitFloorLevel)
                      : intl.formatMessage(messages.unitLevel)
                  }
                  type={removeUnitFloorLevelRestrictions ? 'text' : 'number'}
                  maxLength={removeUnitFloorLevelRestrictions ? undefined : 2}
                  max={25}
                  min={0}
                />
              </HalfField>
              {showUnitPriceField && (
                <HalfField>
                  <Field
                    name="quotingRentAmount"
                    component={renderCLCurrencyField}
                    label={
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          lineHeight: 'inherit',
                        }}
                      >
                        <FormattedMessage {...messages.unitPrice} />
                        <Tooltip
                          title={
                            <FormattedMessage {...messages.unitPriceTooltip} />
                          }
                          placement="top"
                          arrow
                        >
                          <AlertInfoIcon
                            sx={{ fontSize: 16, display: 'block' }}
                          />
                        </Tooltip>
                      </span>
                    }
                    disabled={disableUnitPriceField}
                  />
                </HalfField>
              )}
            </FieldRow>
            {/* Unit ADA? | Accessibility Types */}
            <FieldRow>
              <HalfField>
                <Field
                  name="isADA"
                  component={renderSelectFortress}
                  label={intl.formatMessage(messages.unitADA)}
                  options={ADA_OPTIONS}
                  onSelectChange={handleADAChange}
                  required
                />
              </HalfField>
              {showAccessibilityTypesField && (
                <HalfField>
                  <Field
                    name="accessibilityTypeIds"
                    component={renderMultiSelectFieldFortress}
                    label={intl.formatMessage(messages.accessibilityTypes)}
                    options={accessibilityTypeOptions}
                    placeholder={intl.formatMessage(
                      messages.accessibilityTypesPlaceholder,
                    )}
                    controlledValue={accessibilityTypeIdsValue || []}
                    onChangeCallback={(vals) =>
                      change('accessibilityTypeIds', vals)
                    }
                    selectAllOption
                  />
                </HalfField>
              )}
            </FieldRow>
            {/* Street Address (full width) */}
            <FieldRow>
              <FullField>
                <Field
                  name="streetAddress1"
                  component={renderInputFortress}
                  label="Street Address"
                  placeholder="Address Line 1"
                  required
                />
              </FullField>
            </FieldRow>
            {/* Suite/Apartment | City */}
            <FieldRow>
              <HalfField>
                <Field
                  name="streetAddress2"
                  component={renderInputFortress}
                  label="Suite/Apartment"
                  placeholder="Address Line 2"
                />
              </HalfField>
              <HalfField>
                <Field
                  name="city"
                  component={renderInputFortress}
                  label="City"
                  placeholder="City"
                  required
                />
              </HalfField>
            </FieldRow>
            {/* State | Zip */}
            <FieldRow>
              <HalfField>
                <Field
                  name="state"
                  component={renderSelectFortress}
                  label="State"
                  options={unitOptions.stateOptions}
                  required
                />
              </HalfField>
              <HalfField>
                <Field
                  name="zipCode"
                  component={renderInputFortress}
                  label="Zip"
                  placeholder="Zip Code / Postal Code"
                  required
                />
              </HalfField>
            </FieldRow>
            {/* Alternative Unit Name (XML) */}
            {showXmlUnitNumberField && (
              <FieldRow>
                <HalfField>
                  <Field
                    name="xmlUnitNumber"
                    component={renderInputFortress}
                    label={intl.formatMessage(messages.altXMLUnitNumber)}
                    placeholder="Alternative Unit Name (XML)"
                    maxLength={255}
                  />
                </HalfField>
              </FieldRow>
            )}
            {/* PH Unit Number (PH floorplan only) */}
            {isPHFloorPlan && !featureFlags?.affUnitDetails && (
              <FieldRow>
                <HalfField>
                  <Field
                    name="PHUnitNumber"
                    component={renderInputFortress}
                    label={intl.formatMessage(messages.phUnitNumber)}
                    placeholder={intl.formatMessage(messages.phUnitNumber)}
                    maxLength={10}
                  />
                </HalfField>
              </FieldRow>
            )}
          </Stack>
        </Form>
      )}
    </Fragment>
  );
};

const selector = formValueSelector(FORM_NAME);

const mapStateToProps = (state) => ({
  isADAValue: selector(state, 'isADA'),
  accessibilityTypeIdsValue: selector(state, 'accessibilityTypeIds'),
});

export default injectIntl(
  connect(mapStateToProps)(
    reduxForm({
      form: FORM_NAME,
      validate,
      touchOnChange: true,
    })(GeneralUnitInformationForm),
  ),
);
